import { ServerError } from '@apollo/client';
import { ErrorHandler } from '@apollo/client/link/error';
import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { logError } from 'utils/logError';
import { tooManyRequestsFeedbackModalProps } from './getAxiosTooManyRequestsFeedbackModalProps';

// Message can differ depending on the browser.
const failedToFetchMessages = [
  // Chrome
  'Failed to fetch',
  // Firefox
  'NetworkError when attempting to fetch resource',
];

// Log any GraphQL errors or network error that occurred
export const apolloErrorHandler =
  (
    openFeedbackModal: (props: IFeedbackModal) => void,
    openSessionExpiryModal: () => void,
  ): ErrorHandler =>
  ({ graphQLErrors, networkError }) => {
    if (networkError) {
      if (networkError && (networkError as ServerError).statusCode === 401) {
        openSessionExpiryModal();
        return;
      }

      if (networkError.message === 'Socket closed') {
        logError({
          error: `[Network error]: ${networkError.message}`,
          ignoreSentry: true,
        });
        return;
      }

      // Add specific handling for "Failed to fetch" errors.
      if (
        networkError.message?.startsWith(failedToFetchMessages[0]) ||
        networkError.message?.startsWith(failedToFetchMessages[1])
      ) {
        logError({
          error: `[Network error]: Failed to fetch - Connection issue detected`,
        });
        return;
      }

      logError({
        error: `[Network error]: ${JSON.stringify(networkError)}`,
      });

      if (networkError && (networkError as ServerError).statusCode === 429) {
        openFeedbackModal(tooManyRequestsFeedbackModalProps);
        return;
      }
    }

    const errors = graphQLErrors?.map(
      ({ message, locations, path }) =>
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations,
        )}, Path: ${JSON.stringify(path)}`,
    );

    errors?.forEach((error) => logError({ error }));

    const is429GraphQLError = !!graphQLErrors?.some(
      (gqlError) => gqlError.extensions?.statusCode === 429,
    );

    if (is429GraphQLError) {
      openFeedbackModal(tooManyRequestsFeedbackModalProps);
      return;
    }
  };
